/**
 * DimensionFilters — a single, compact "Filters" popover for all dimension
 * multi-selects (department/status/severity/priority/…), instead of one inline
 * dropdown per dimension. Keeps the filter bar elegant: just Period + one
 * Filters button with an active-count badge.
 *
 * Config-driven: pass `dims` = [{ key, label }]. Options are fetched per
 * dimension from /v2/filter-options on first expand. Selections are reported
 * via onChange(key, values); onClearAll clears everything.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Filter, ChevronDown, Check, X } from 'lucide-react';
import { fetchFilterOptions } from '../api/client';

// One collapsible dimension section inside the Filters popover.
function DimSection({ module, dim, values = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null); // null = not loaded yet

  useEffect(() => {
    if (!open || options !== null) return undefined;
    let live = true;
    fetchFilterOptions(module, dim.key)
      .then((opts) => { if (live) setOptions(opts); })
      .catch(() => { if (live) setOptions([]); });
    return () => { live = false; };
  }, [open, options, module, dim.key]);

  const selectedSet = new Set(values);
  const toggle = (val) => {
    const next = selectedSet.has(val) ? values.filter((v) => v !== val) : [...values, val];
    onChange(dim.key, next);
  };

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-sm hover:bg-gray-50"
      >
        <span className="font-medium text-gray-700">{dim.label}</span>
        <span className="flex items-center gap-2">
          {values.length > 0 && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">{values.length}</span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="max-h-44 overflow-y-auto px-2 pb-2">
          {options === null && <div className="px-2 py-2 text-sm text-gray-400">Loading…</div>}
          {options !== null && options.length === 0 && (
            <div className="px-2 py-2 text-sm text-gray-400">No options</div>
          )}
          {(options || []).map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
              <input type="checkbox" checked={selectedSet.has(o.value)} onChange={() => toggle(o.value)} />
              <span className="truncate">{o.label}</span>
              {selectedSet.has(o.value) && <Check className="ml-auto h-3.5 w-3.5 text-blue-600" />}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DimensionFilters({ module, dims = [], selected = {}, onChange, onClearAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Badge counts the number of dimensions with an active selection.
  const activeCount = dims.reduce((n, d) => n + ((selected[d.key] || []).length ? 1 : 0), 0);

  return (
    <div className="relative flex flex-col" ref={ref}>
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Filters</span>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`mt-1 flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${
          activeCount > 0 ? 'border-blue-300 bg-blue-50/50 text-blue-700' : 'border-gray-300 bg-white text-gray-700'
        }`}
      >
        <Filter className="h-4 w-4 text-gray-400" />
        <span>Filters</span>
        {activeCount > 0 && (
          <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold leading-none text-white">{activeCount}</span>
        )}
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-72 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-80 overflow-y-auto">
            {dims.map((dim) => (
              <DimSection
                key={dim.key}
                module={module}
                dim={dim}
                values={selected[dim.key] || []}
                onChange={onChange}
              />
            ))}
          </div>
          {activeCount > 0 && onClearAll && (
            <div className="border-t border-gray-100 p-1">
              <button
                type="button"
                onClick={onClearAll}
                className="flex w-full items-center gap-1 rounded px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
              >
                <X className="h-3.5 w-3.5" /> Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
